import { LocalStorage } from "./LocalStorage.js";
import { ServiceConsts } from "./ServiceConsts.js";
import { UserService } from "$/services/UserService";
import { EventService } from "$/services/EventService";


export const AUTH_NAME = "COLLABWRITING_AUTH";
const REFRESH_LOCK = "COLLABWRITING_REFRESH_LOCK";
const REFRESH_LOCK_EXPIRATION = "COLLABWRITING_REFRESH_LOCK_EXPIRATION";

const EXPIRATION_BUFFER = 180;
const EXPIRATION = 3600;

export class AutoRefresh {
    static interval;

    static init = async () => {
        console.log("Auto refresh init");

        await LoginService.autoRefreshToken();
        this.start();
    };

    static start = () => {
        this.stop();

        console.log("Interval set");

        /**
         * NOTE: There's a problem with interval if start is called in the middle
         *       of JWT expiration, because interval does not take in account the
         *       actual time of token expiration but only a relative time of token's
         *       duration. For all this to work, times of refresh should be tide to
         *       actual JWT expiration (Ex. 14:53:12.035)
         */
        this.interval = setInterval(
            () => {
                console.log("Interval called");
                LoginService.autoRefreshToken();
            },
            (EXPIRATION - EXPIRATION_BUFFER) * 1000,
        );
    };

    static stop = () => {
        console.log("Interval cleared");

        if (this.interval) {
            clearInterval(this.interval);
        }
    };
}

export class LoginService {
    static async authCodeLogin(id, code) {
        let requestUrl =
            ServiceConsts.SERVER_URL +
            "/auth/token/code" +
            "?id=" +
            id +
            "&code=" +
            code;
        let response = await fetch(requestUrl, ServiceConsts.HTTP_GET);

        if (response.ok) {
            let data = await response.json();

            await LoginService.saveAuth(data);

            //AutoRefresh.start();
            EventService.trackEvent("login");


            return data;
        } else {
            throw new Error(404);
        }
    }

    static async autoRefreshToken() {
        let auth = await LocalStorage.get(AUTH_NAME);

        if (auth) {
            await LoginService.refreshTokenLogin(auth.refresh);
        }
    }

    static async refreshTokenLogin() {
        //TODO: Figure out a better syncronisation alternative. Read more at:
        //      https://stackoverflow.com/questions/59180817/race-condition-trying-to-sync-access-token-in-multiple-tab
        //
        //      Maybe randomized Auto Refresh with a semaphore lock would be a better alternative because no request
        //      would wait for a random amount of ms to be handeled. And there is a bigger time frame, because when it
        //      comes to Auto Refresh we can litteraly choose any moment in one hour to refresh a token instead of 500ms
        //      like now
        //
        //      Auto Refresh should be initialized on page load if token exists, or authCodeLogin if not.
        //
        //      The known problem with auto refresh is that TokenStore calls for getAuth before the auto refresh is updated
        //
        //      TokenStore maybe should be subscribed to LocalStorage instead, so when new auth is received it is updated.
        //      That way even TokenStoreSync is not necessary for whole system to work.
        //
        //      With Auto Refresh, refreshTokenLogin function can stay exactly the same with semaphores just function call
        //      in getAuth should be removed and moved to Auto Refresh
        //
        const delay = (ms) => new Promise((res) => setTimeout(res, ms));
        await delay(Math.floor(Math.random() * 500));

        let refreshLocked = await LocalStorage.get(REFRESH_LOCK);
        let refreshExpiration = await LocalStorage.get(REFRESH_LOCK_EXPIRATION);
        let refreshExpired = Date.now() > refreshExpiration;

        // other process is already in a middle of refreshing token
        if (refreshLocked && !refreshExpired) {
            while (refreshLocked) {
                await delay(100);
                refreshLocked = await LocalStorage.get(REFRESH_LOCK);
            }

            let auth = await LocalStorage.get(AUTH_NAME);

            return auth;
        }

        // expires in 120 seconds
        let expiresIn = Date.now() + 120000;

        refreshLocked = await LocalStorage.set(REFRESH_LOCK, true);
        refreshExpiration = await LocalStorage.set(
            REFRESH_LOCK_EXPIRATION,
            expiresIn,
        );

        let auth = await LocalStorage.get(AUTH_NAME);
        let refresh = auth.refresh;

        try {
            let requestUrl =
                ServiceConsts.SERVER_URL +
                "/auth/token/refresh" +
                "?id=" +
                refresh.id +
                "&nonce=" +
                refresh.nonce +
                "&userId=" +
                refresh.userId;

            let response = await fetch(requestUrl, ServiceConsts.HTTP_GET);

            if (response.ok) {
                let data = await response.json();

                await LoginService.saveAuth(data);

                return data;
            } else {
                /**
                 * NOTE: Maybe auth shoudn't be cleared on error auth is expiered nevertheless
                 * it will be refreshed maybe in the future
                 * Maybe just a wait should exist to wait for refresh to happen.
                 *
                 * Or a check to figure out if refresh token is expiered or invalid (2 times same token)
                 */

                // expired or invalid refresh token
                await LoginService.clearAuth();
                let error = await response.json();

                throw new Error(error);
            }
        } finally {
            await LocalStorage.remove(REFRESH_LOCK);
        }
    }

    static async getAuth() {
        let auth = await LocalStorage.get(AUTH_NAME);

        if (auth == null) throw new Error(404);

        const expired = LoginService.isExpired(auth.exp);

        if (expired) {
            auth = await LoginService.refreshTokenLogin();
        }

        return auth;
    }

    static async saveAuth(auth) {
        return await LocalStorage.set(AUTH_NAME, auth);
    }

    static async clearAuth() {
        return await LocalStorage.remove(AUTH_NAME);
    }

    static isExpired = (exp) =>
        !exp ? true : Date.now() > (exp - EXPIRATION_BUFFER) * 1000;

    static async getMockToken() {
        return "eyJhbGciOiJSUzUxMiJ9.eyJzdWIiOiJpdmFuLnoucmFsaWNAZ21haWwuY29tIiwiZXhwIjoxNjUxMDM2MDIwLCJpc3MiOiJpbmZvQGNvbGxhYndyaXRpbmcuY29tIiwidXNlciI6eyJpZCI6IjhiN2I3ZGQ5LWQzMDgtNDgyNS04NjEyLWMxYTE3NGM1YzhiOCIsInVzZXJuYW1lIjoiaXZhbi56LnJhbGljQGdtYWlsLmNvbSJ9LCJhdXRob3JpdGllcyI6WyJST0xFX0FETUlOIiwiUk9MRV9VU0VSIl0sInRlbmFudCI6IjhmMzdjZmRjLTI0ZTQtNGY5MS1iN2E0LTBhMzUzM2MzYzYzZiJ9.JPJ0AxNyYN0rKCFQ0YxrG-8wpPQOtkr0YYz3krpm0L5BgL61BbjkLEpi4osonKrosQtw5zTvSfgccBlo03BnEA-wN1Qy0SHIa1ZEZ7RzUqHbY69U9hOCxZnpiKADAKBQGLNN3eDulKSi03XN_FOnWIquWI4OqZksPEq20tHulaEG5peXIHiwKHv4_ztqHtXrckaOkFPaOti7Pwv44l_khhZMzf5lwSOpaHGLphB5Ej39jq6kiZxUTASeVTSEVs6ZTjy1zt-vfSas1zRcF6tYCkLJmavdELQcDl_3nRUF9Btn2XUQ3MRRausLbhymjpk1mP5Sb9_7EN2p_AoKnI2Gdw&nonce=xznfcm6rqgvh93kknzawesje3qs50x8bdsafqwo867xhr9m3jn636bb44i61esti&id=d03cc67d-7783-4558-8813-1758b2862c93";
    }
}
